/**
 * PiataCont — contul vânzătorului/cumpărătorului: produse, vânzări (comision 1%), comenzi.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, Pencil, Trash2, PauseCircle, PlayCircle, Store,
  BadgePercent, Wallet, HandCoins, PackageOpen, Phone, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import PiataHeader from '../components/PiataHeader';
import SiteFooter from '../components/SiteFooter';
import useSEO from '../hooks/useSEO';
import api from '../lib/api';
import { catMeta, imgUrl, fmtRON, ORDER_STATUS_LABELS, ORDER_STATUS_STYLES } from '../lib/piata';

const TABS = [
  { id: 'produse', label: 'Produsele mele' },
  { id: 'vanzari', label: 'Vânzări' },
  { id: 'comenzi', label: 'Comenzile mele' },
];

export default function PiataCont() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('produse');
  const [products, setProducts] = useState(null);
  const [sales, setSales] = useState(null);
  const [orders, setOrders] = useState(null);

  useSEO({ title: 'Contul meu · Piața micilor producători · Energy Project Design' });

  const loadAll = useCallback(() => {
    api.get('/piata/my/products').then(({ data }) => setProducts(data.items)).catch(() => setProducts([]));
    api.get('/piata/my/sales').then(({ data }) => setSales(data)).catch(() => setSales({ orders: [], summary: {} }));
    api.get('/piata/my/orders').then(({ data }) => setOrders(data.items)).catch(() => setOrders([]));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleStatus = async (p) => {
    const next = p.status === 'active' ? 'suspendat' : 'active';
    try {
      await api.put(`/piata/products/${p.product_id}`, { status: next });
      toast.success(next === 'active' ? 'Produs reactivat' : 'Produs suspendat');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Eroare'); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Ștergi definitiv „${p.title}"?`)) return;
    try {
      await api.delete(`/piata/products/${p.product_id}`);
      toast.success('Produs șters');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Eroare'); }
  };

  const updateOrderStatus = async (o, status) => {
    try {
      await api.put(`/piata/orders/${o.order_id}/status`, { status });
      toast.success(`Comandă marcată: ${ORDER_STATUS_LABELS[status]}`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Eroare'); }
  };

  const summary = sales?.summary || {};

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="page-piata-cont">
      <PiataHeader active="cont" />

      <div className="pt-24 pb-16 max-w-6xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-zinc-500 font-bold mb-2">// contul meu de producător</div>
            <h1 className="text-3xl lg:text-4xl font-bold tracking-[-0.03em] text-zinc-950 font-display">
              Piața <span className="italic text-zinc-400 font-normal">producătorului.</span>
            </h1>
          </div>
          <Link to="/piata/vinde" className="epd-btn text-sm py-2" data-testid="cont-new-product">+ Publică produs gratuit</Link>
        </div>

        <div className="flex gap-1 border-b border-zinc-200 mb-8">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-zinc-950 text-zinc-950' : 'border-transparent text-zinc-500 hover:text-zinc-950'}`} data-testid={`cont-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ===== PRODUSELE MELE ===== */}
        {tab === 'produse' && (
          <div data-testid="cont-products-panel">
            {products === null && <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" /></div>}
            {products?.length === 0 && (
              <div className="text-center py-16 text-zinc-400 border border-dashed border-zinc-300 rounded-md" data-testid="cont-products-empty">
                <PackageOpen className="w-10 h-10 mx-auto mb-3 opacity-60" strokeWidth={1.2} />
                <div className="text-sm font-semibold text-zinc-600 mb-4">Nu ai publicat încă niciun produs</div>
                <Link to="/piata/vinde" className="epd-btn text-sm py-2 inline-flex">Publică primul produs gratuit</Link>
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products?.map((p) => {
                const meta = catMeta(p.category);
                const Icon = meta.icon;
                return (
                  <div key={p.product_id} className="border border-zinc-200 rounded-md overflow-hidden hover:border-zinc-400 transition-colors" data-testid={`cont-product-${p.product_id}`}>
                    <div className="h-32 bg-zinc-100 relative">
                      {p.images?.[0] ? (
                        <img src={imgUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Icon className="w-8 h-8 text-zinc-300" strokeWidth={1.2} /></div>
                      )}
                      <span className={`absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full border ${p.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-zinc-100 text-zinc-500 border-zinc-300'}`}>
                        {p.status === 'active' ? 'Activ' : 'Suspendat'}
                      </span>
                    </div>
                    <div className="p-3.5">
                      <div className="text-sm font-bold text-zinc-950 leading-tight mb-1 line-clamp-1">{p.title}</div>
                      <div className="text-xs text-zinc-500 mb-2">{fmtRON(p.price_ron)} / {p.unit} · stoc {p.stock} · {p.sales_count || 0} vânzări · {p.views || 0} vizualizări</div>
                      <div className="flex gap-1.5">
                        <button onClick={() => navigate(`/piata/vinde?edit=${p.product_id}`)} className="flex-1 inline-flex items-center justify-center gap-1 border border-zinc-300 hover:border-zinc-950 rounded-md py-1.5 text-[11px] font-semibold transition-colors" data-testid={`cont-edit-${p.product_id}`}>
                          <Pencil className="w-3 h-3" /> Editează
                        </button>
                        <button onClick={() => toggleStatus(p)} className="flex-1 inline-flex items-center justify-center gap-1 border border-zinc-300 hover:border-zinc-950 rounded-md py-1.5 text-[11px] font-semibold transition-colors" data-testid={`cont-toggle-${p.product_id}`}>
                          {p.status === 'active' ? <><PauseCircle className="w-3 h-3" /> Suspendă</> : <><PlayCircle className="w-3 h-3" /> Activează</>}
                        </button>
                        <button onClick={() => deleteProduct(p)} className="inline-flex items-center justify-center border border-zinc-300 hover:border-red-500 hover:text-red-600 rounded-md px-2.5 py-1.5 transition-colors" data-testid={`cont-delete-${p.product_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== VÂNZĂRI ===== */}
        {tab === 'vanzari' && (
          <div data-testid="cont-sales-panel">
            {sales === null && <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" /></div>}
            {sales && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                  <div className="border border-zinc-200 rounded-md p-4" data-testid="cont-sum-gross">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1"><Wallet className="w-3.5 h-3.5" /> Vânzări card (brut)</div>
                    <div className="text-xl font-bold tabular-nums font-display">{fmtRON(summary.gross_card_ron)}</div>
                  </div>
                  <div className="border border-zinc-200 rounded-md p-4" data-testid="cont-sum-commission">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1"><BadgePercent className="w-3.5 h-3.5" /> Comision EPD (1%)</div>
                    <div className="text-xl font-bold tabular-nums font-display text-violet-700">{fmtRON(summary.commission_card_ron)}</div>
                  </div>
                  <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-4" data-testid="cont-sum-net">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1"><Store className="w-3.5 h-3.5" /> Net de încasat (99%)</div>
                    <div className="text-xl font-bold tabular-nums font-display text-emerald-800">{fmtRON(summary.net_card_ron)}</div>
                  </div>
                  <div className="border border-zinc-200 rounded-md p-4" data-testid="cont-sum-direct">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1"><HandCoins className="w-3.5 h-3.5" /> Comenzi directe</div>
                    <div className="text-xl font-bold tabular-nums font-display">{fmtRON(summary.direct_total_ron)}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">comision 1% datorat: {fmtRON(summary.commission_direct_due_ron)}</div>
                  </div>
                </div>

                {sales.orders.length === 0 ? (
                  <div className="text-center py-12 text-zinc-400 border border-dashed border-zinc-300 rounded-md text-sm" data-testid="cont-sales-empty">Nicio vânzare încă. Distribuie link-ul produselor tale!</div>
                ) : (
                  <div className="space-y-3">
                    {sales.orders.map((o) => (
                      <div key={o.order_id} className="border border-zinc-200 rounded-md p-4 flex flex-wrap items-center gap-4" data-testid={`cont-sale-${o.order_id}`}>
                        <div className="flex-1 min-w-[220px]">
                          <div className="text-sm font-bold text-zinc-950">{o.product_title} <span className="text-zinc-400 font-normal">× {o.qty} {o.unit}</span></div>
                          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-3 flex-wrap">
                            <span>{o.buyer_name}</span>
                            <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{o.buyer_phone}</span>
                            {o.buyer_address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{o.buyer_address}</span>}
                          </div>
                          {o.note && <div className="text-[11px] text-zinc-500 italic mt-1">„{o.note}"</div>}
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold tabular-nums">{fmtRON(o.total_ron)}</div>
                          <div className="text-[10px] text-zinc-500">comision {fmtRON(o.commission_ron)} · net {fmtRON(o.net_ron)}</div>
                          <div className={`text-[10px] font-bold mt-0.5 ${o.payment_method === 'card' ? (o.payment_status === 'paid' ? 'text-emerald-600' : 'text-amber-600') : 'text-zinc-600'}`}>
                            {o.payment_method === 'card' ? (o.payment_status === 'paid' ? '● Plătită cu cardul' : '○ Plată card neconfirmată') : '● Comandă directă'}
                          </div>
                        </div>
                        <select value={o.status} onChange={(e) => updateOrderStatus(o, e.target.value)} className={`border rounded-md px-2.5 py-1.5 text-xs font-semibold ${ORDER_STATUS_STYLES[o.status] || 'border-zinc-300'}`} data-testid={`cont-sale-status-${o.order_id}`}>
                          {Object.entries(ORDER_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ===== COMENZILE MELE (cumpărător) ===== */}
        {tab === 'comenzi' && (
          <div data-testid="cont-orders-panel">
            {orders === null && <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" /></div>}
            {orders?.length === 0 && (
              <div className="text-center py-12 text-zinc-400 border border-dashed border-zinc-300 rounded-md text-sm" data-testid="cont-orders-empty">
                Nu ai plasat nicio comandă. <Link to="/piata" className="text-violet-700 font-semibold hover:underline">Explorează piața →</Link>
              </div>
            )}
            <div className="space-y-3">
              {orders?.map((o) => (
                <div key={o.order_id} className="border border-zinc-200 rounded-md p-4 flex flex-wrap items-center gap-4" data-testid={`cont-order-${o.order_id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="text-sm font-bold text-zinc-950">{o.product_title} <span className="text-zinc-400 font-normal">× {o.qty} {o.unit}</span></div>
                    <div className="text-xs text-zinc-500 mt-0.5">Producător: {o.seller_name} · {new Date(o.created_at).toLocaleDateString('ro-RO')}</div>
                  </div>
                  <div className="text-sm font-bold tabular-nums">{fmtRON(o.total_ron)}</div>
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${ORDER_STATUS_STYLES[o.status] || ''}`}>{ORDER_STATUS_LABELS[o.status] || o.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}
